import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BulkImportHistory } from "@/features/bulk-imports/components/BulkImportHistory";
import { BulkImportPreview } from "@/features/bulk-imports/components/BulkImportPreview";
import { BulkImportUploader } from "@/features/bulk-imports/components/BulkImportUploader";
import {
  useBulkImportHistory,
  useBulkImportTypes,
  type CreateBulkImportBatchResult,
} from "@/features/bulk-imports";

type BulkImportTab = "upload" | "history";

export function BulkImportPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BulkImportTab>("upload");
  const [previewResult, setPreviewResult] =
    useState<CreateBulkImportBatchResult | null>(null);

  const {
    types,
    isLoading: isLoadingTypes,
    error: typesError,
    refetch: refetchTypes,
  } = useBulkImportTypes();
  const {
    batches,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory,
  } = useBulkImportHistory({ limit: 25 });

  const refetchAll = () => {
    refetchTypes();
    refetchHistory();
  };

  const handleUploaded = (result: CreateBulkImportBatchResult) => {
    setPreviewResult(result);
    refetchHistory();
    setActiveTab("upload");
  };

  const openBatch = (batchId: string) => {
    navigate(`/contabilidad/importaciones/${batchId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Importaciones contables</h1>
          <p className="text-sm text-muted-foreground">
            Carga masiva de contactos, comprobantes, ingresos, egresos y setups.
          </p>
        </div>
        <Button variant="outline" onClick={refetchAll}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Actualizar
        </Button>
      </div>

      {(typesError || historyError) && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">No se pudo cargar toda la informacion</p>
            <p className="text-destructive/80">
              {typesError?.message ?? historyError?.message}
            </p>
          </div>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as BulkImportTab)}
        className="space-y-4"
      >
        <div className="overflow-x-auto">
          <TabsList>
            <TabsTrigger value="upload">Carga</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="upload" className="space-y-4">
          <BulkImportUploader
            types={types}
            isLoadingTypes={isLoadingTypes}
            onUploaded={handleUploaded}
          />
          <BulkImportPreview result={previewResult} onOpenBatch={openBatch} />
        </TabsContent>

        <TabsContent value="history">
          <BulkImportHistory
            batches={batches}
            isLoading={isLoadingHistory}
            error={historyError}
            selectedBatchId={null}
            onSelectBatch={openBatch}
            onRefresh={refetchHistory}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
